import { createContext, useContext, useEffect, useId } from 'react';
import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from '../utils/cn';
import { glassFocusRing, glassRadius } from './GlassSurface';
import { springLayout, springSnappy } from './motion';

/** Layout constants so a screen can pad itself clear of the rail. */
export const GLASS_NAV_WIDTH = 248;
export const GLASS_NAV_COLLAPSED_WIDTH = 76;

interface GlassNavContextValue {
  collapsed: boolean;
  activeId: string | undefined;
  onSelect: ((id: string) => void) | undefined;
  layoutId: string;
}

const GlassNavContext = createContext<GlassNavContextValue>({
  collapsed: false,
  activeId: undefined,
  onSelect: undefined,
  layoutId: 'g-nav-active',
});

export interface GlassNavProps {
  activeId?: string;
  onSelect?: (id: string) => void;
  collapsed?: boolean;
  /** Supplying this renders the collapse control; the rail stays controlled. */
  onToggle?: () => void;
  /** Brand mark, product switcher — anything above the item list. */
  header?: ReactNode;
  footer?: ReactNode;
  /** Distance from the viewport edges. The rail floats; it is not full-bleed. */
  inset?: number;
  /** Set false to place the rail in normal flow, for stories and tests. */
  floating?: boolean;
  /**
   * Drawer mode. The rail leaves the layout entirely and slides over the
   * content instead of displacing it — the only workable pattern under about
   * 640px, where a persistent rail costs a quarter of the screen and an
   * icon-only rail costs the labels that make it navigable.
   */
  overlay?: boolean;
  /** Drawer visibility. Ignored unless `overlay`. */
  open?: boolean;
  /** Escape, scrim click, or selecting an item. Ignored unless `overlay`. */
  onDismiss?: () => void;
  label?: string;
  className?: string;
  children?: ReactNode;
}

/**
 * The floating navigation rail — glass, because it hovers over content rather
 * than holding any.
 */
export function GlassNav({
  activeId,
  onSelect,
  collapsed = false,
  onToggle,
  header,
  footer,
  inset = 12,
  floating = true,
  overlay = false,
  open = false,
  onDismiss,
  label = 'Primary',
  className,
  children,
}: GlassNavProps) {
  const layoutId = useId();

  // Escape closes the drawer. Bound only while it is open, so the key stays
  // available to dialogs and menus the rest of the time.
  useEffect(() => {
    if (!overlay || !open || !onDismiss) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlay, open, onDismiss]);

  // In drawer mode the rail always shows its labels. Collapsing is a
  // desktop affordance for reclaiming width; a drawer has already reclaimed it.
  const effectiveCollapsed = overlay ? false : collapsed;

  return (
    <GlassNavContext.Provider
      value={{ collapsed: effectiveCollapsed, activeId, onSelect, layoutId }}
    >
      {overlay ? (
        <AnimatePresence>
          {open ? (
            <motion.div
              className="fixed inset-0 z-30"
              style={{ background: 'rgba(15, 23, 42, 0.42)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onDismiss}
              aria-hidden
            />
          ) : null}
        </AnimatePresence>
      ) : null}

      <motion.nav
        aria-label={label}
        aria-hidden={overlay && !open}
        className={cn('g-surface z-40 flex flex-col overflow-hidden', className)}
        animate={
          overlay
            ? { width: GLASS_NAV_WIDTH, x: open ? 0 : -(GLASS_NAV_WIDTH + inset + 8) }
            : { width: collapsed ? GLASS_NAV_COLLAPSED_WIDTH : GLASS_NAV_WIDTH, x: 0 }
        }
        transition={springLayout}
        style={{
          borderRadius: glassRadius.xl,
          // A closed drawer must not be reachable by keyboard or pointer; it is
          // off-screen, not hidden, and `visibility` is what tells the browser.
          ...(overlay && !open ? { visibility: 'hidden' as const } : null),
          ...(floating
            ? { position: 'fixed', left: inset, top: inset, bottom: inset }
            : { position: 'relative' }),
        }}
      >
        {header ? (
          <div className="flex shrink-0 items-center gap-3 px-4 pb-2 pt-5">{header}</div>
        ) : null}

        <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-3">{children}</ul>

        {footer || onToggle ? (
          <div
            className="flex shrink-0 items-center gap-2 px-3 pb-4 pt-3"
            style={{ borderTop: '1px solid var(--g-edge)' }}
          >
            {footer ? <div className="min-w-0 flex-1">{footer}</div> : null}
            {onToggle ? (
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={!collapsed}
                aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
                className={cn(
                  'ml-auto flex h-8 w-8 shrink-0 items-center justify-center',
                  glassFocusRing,
                )}
                style={{
                  borderRadius: glassRadius.sm,
                  color: 'var(--g-text-muted)',
                  border: '1px solid var(--g-edge)',
                }}
              >
                <motion.svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  animate={{ rotate: collapsed ? 180 : 0 }}
                  transition={springSnappy}
                >
                  <path d="M15 5l-7 7 7 7" />
                </motion.svg>
              </button>
            ) : null}
          </div>
        ) : null}
      </motion.nav>
    </GlassNavContext.Provider>
  );
}

export interface GlassNavItemProps {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Count or status chip, hidden when the rail is collapsed. */
  badge?: ReactNode;
  /** Renders an anchor instead of a button. */
  href?: string;
  disabled?: boolean;
  onSelect?: (id: string) => void;
  className?: string;
}

export function GlassNavItem({
  id,
  label,
  icon,
  badge,
  href,
  disabled = false,
  onSelect,
  className,
}: GlassNavItemProps) {
  const context = useContext(GlassNavContext);
  const active = context.activeId === id;
  const collapsed = context.collapsed;

  const handleSelect = () => {
    if (disabled) return;
    onSelect?.(id);
    context.onSelect?.(id);
  };

  const inner = (
    <>
      {/*
        A shared layoutId means the highlight is one object sliding between
        items rather than one fading out while another fades in. It is the
        detail people notice without being able to name.
      */}
      {active ? (
        <motion.span
          layoutId={context.layoutId}
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            borderRadius: glassRadius.md,
            background: 'color-mix(in oklab, var(--g-accent) 18%, transparent)',
            border: '1px solid color-mix(in oklab, var(--g-accent) 34%, transparent)',
            boxShadow: 'var(--g-specular)',
          }}
          transition={springLayout}
        />
      ) : null}

      {icon ? (
        <span
          aria-hidden="true"
          className="relative flex h-5 w-5 shrink-0 items-center justify-center"
        >
          {icon}
        </span>
      ) : null}

      <AnimatePresence initial={false}>
        {!collapsed ? (
          <motion.span
            key="label"
            className="relative min-w-0 flex-1 truncate text-[13.5px]"
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -4 }}
            transition={springSnappy}
          >
            {label}
          </motion.span>
        ) : null}
      </AnimatePresence>

      {!collapsed && badge ? <span className="relative shrink-0">{badge}</span> : null}
      {collapsed ? <span className="sr-only">{label}</span> : null}
    </>
  );

  const classes = cn(
    'relative flex w-full items-center gap-3 px-3 py-2.5 font-medium transition-colors',
    disabled && 'pointer-events-none opacity-40',
    glassFocusRing,
    className,
  );
  const style = {
    borderRadius: glassRadius.md,
    color: active ? 'var(--g-text)' : 'var(--g-text-secondary)',
  };

  return (
    <li className="min-w-0">
      {href ? (
        <a
          href={href}
          aria-current={active ? 'page' : undefined}
          title={collapsed ? label : undefined}
          onClick={handleSelect}
          className={classes}
          style={style}
        >
          {inner}
        </a>
      ) : (
        <button
          type="button"
          aria-current={active ? 'page' : undefined}
          title={collapsed ? label : undefined}
          disabled={disabled}
          onClick={handleSelect}
          className={classes}
          style={style}
        >
          {inner}
        </button>
      )}
    </li>
  );
}
